import { normalize } from 'normalizr';

import {
  type AuthIdentityProviderSchema,
  httpDeleteAuthSamlIdpByIdentityProviderId,
  httpGetAuthSamlIdp,
  httpGetAuthSamlIdpByIdentityProviderId,
  httpPostAuthSamlIdp,
  httpPostAuthSamlIdpConvert,
  httpPutAuthSamlIdpByIdentityProviderId,
} from '@iconik-io/iconik-api/src/services/auth';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';

import {
  ADMIN_IDENTITY_PROVIDER_ROUTE,
  ADMIN_IDENTITY_PROVIDERS_LIST_ROUTE,
} from '../../constants/routes';
import type { AppDispatch, GetState } from '../../store/types';
import {
  type CommonListGetParams,
  wrapCommonCreate,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../common_list/thunks';
import { historyPush } from '../history/actions';
import { errorMessage, successMessage } from '../messages/thunks';

export function identityProvidersCreate(data: AuthIdentityProviderSchema) {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'identity_providers',
      dispatch,
      createPromise: dispatch(httpPostAuthSamlIdp(data)),
    }).then(() => {
      dispatch(successMessage({ text: 'Identity Provider created' }));
    });
}

export function identityProvidersEdit(
  id: string,
  data: AuthIdentityProviderSchema,
) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      httpPutAuthSamlIdpByIdentityProviderId(id, data),
    );
    const { entities } = normalize(
      [response.body],
      [entitySchemas.identity_providers],
    );
    dispatch(successMessage({ text: 'Identity Provider updated' }));
    dispatch(updateEntities(entities));
  };
}

export function identityProvidersGet(params: CommonListGetParams = {}) {
  return (dispatch: AppDispatch) =>
    wrapCommonGetList({
      prefix: 'identity_providers',
      dispatch,
      params,
      getListPromise: () => dispatch(httpGetAuthSamlIdp()),
    });
}

export function identityProvidersDelete([id]: string[]) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    wrapCommonDelete({
      prefix: 'identity_providers',
      dispatch,
      ids: [id],
      deletePromise: Promise.all(
        [id].map((selectedId) =>
          dispatch(httpDeleteAuthSamlIdpByIdentityProviderId(selectedId)),
        ),
      ),
    }).then(() => {
      dispatch(
        successMessage({
          text: 'Identity Provider was deleted',
          persistOnNavigation: true,
        }),
      );
      const route = getState().routeParams.route;
      if (route === ADMIN_IDENTITY_PROVIDER_ROUTE) {
        dispatch(historyPush(ADMIN_IDENTITY_PROVIDERS_LIST_ROUTE));
      }
    });
}
export function identityProviderGet(id: string) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(httpGetAuthSamlIdpByIdentityProviderId(id));
    const { entities } = normalize(
      [response.body],
      [entitySchemas.identity_providers],
    );
    return dispatch(updateEntities(entities));
  };
}

export function identityProvidersConvert(data: string) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPostAuthSamlIdpConvert(data, {
        'Content-Type': 'application/xml',
      }),
    ).catch((error) => {
      dispatch(errorMessage({ text: handleErrors(error) }));
      throw error;
    });
}
